// --- Schedule Maintenance Models ---

export enum MaintIntervalType {
  Days = 1,
  Months = 2,
  Hours = 3,
  Years = 4,
  Week = 5
}

export interface MaintIntervalTypeLookupDto {
  id: number;
  name: string;
  code: string;
}

export interface MaintenanceAssetScheduleDto {
  id: number;
  maintenanceCode: string;
  assetId: number;
  assetNumber: string;
  assetName: string;
  maintenanceTypeId?: number;
  maintenanceTypeName?: string;
  maintenanceTypeCode?: string;
  scheduledFor?: string | Date;
  scheduledOn?: string | Date;
  personId?: number;
  personName?: string;
  isRecurring: boolean;
  timeInterval?: boolean;
  maintenanceIntervalId?: number;
  maintenanceIntervalName?: string;
  maintIntervalType?: MaintIntervalType | string;
  maintIntervalTypeName?: string;
  scheduledBased?: boolean;
  frequency?: number;
  dueNext?: string | Date;
  lastPerformed?: string | Date;
  startDate?: string | Date;
  endDate?: string | Date;
  notes?: string;
  siteId?: number;
  isPerformed: boolean;
  isOverdue: boolean;
  yearsToForcastStockQuantity?: number;
  isFlagged?: boolean;
  scheduledForOtherInterval?: number;
  recurringBasis?: string | Date;
  recurringBasisOtherInterval?: number;
  dueNextOthers?: number;
  createDate?: string | Date;
  lastModifyDate?: string | Date;
  documentFileIds?: number[];
  files?: any[];
  displayTitle?: string;
  stockParts?: MaintenanceScheduleStockDto[];

  // Relational properties matching GridFieldDefinitions navigation paths
  asset?: {
    id?: number;
    assetNumber?: string;
    assetName?: string;
    serialNumber?: string;
    endOfLife?: string | Date;
    endOfSupport?: string | Date;
    lastPhysicalInventoryDate?: string | Date;
    model?: {
      id?: number;
      number?: string;
      name?: string;
      modelInfo?: {
        id?: number;
        category?: {
          id?: number;
          code?: string;
          description?: string;
        };
      };
    };
    location?: {
      id?: number;
      code?: string;
      description?: string;
    };
    site?: {
      id?: number;
      code?: string;
      name?: string;
    };
    fundingSource?: {
      id?: number;
      fundingSourceId?: string;
    };
  };
  maintenanceType?: {
    id?: number;
    maintenanceTypeCode?: string;
    maintenanceTypeName?: string;
  };
  maintenanceInterval?: {
    id?: number;
    maintenanceIntervalCode?: string;
    maintenanceIntervalName?: string;
  };
  person?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    code?: string;
  };
  site?: {
    id?: number;
    code?: string;
    name?: string;
  };
}

export interface MaintenanceAssetScheduleRequestModel {
  id?: number;
  maintenanceCode?: string;
  assetId: number;
  maintenanceTypeId?: number;
  scheduledFor?: string | Date;
  scheduledOn?: string | Date;
  personId?: number;
  isRecurring: boolean;
  timeInterval?: boolean;
  maintenanceIntervalId?: number;
  maintIntervalType?: MaintIntervalType | string;
  scheduledBased?: boolean;
  frequency?: number;
  dueNext?: string | Date;
  startDate?: string | Date;
  endDate?: string | Date;
  notes?: string;
  siteId?: number;
  documentFileIds?: number[];
  stockParts?: MaintenanceScheduleStockRequestModel[];
  yearsToForcastStockQuantity?: number;
  isFlagged?: boolean;
  scheduledForOtherInterval?: number;
  recurringBasis?: string | Date;
  recurringBasisOtherInterval?: number;
  dueNextOthers?: number;
}

export interface MaintenanceTypeDto {
  id: number;
  maintenanceTypeCode: string;
  maintenanceTypeName?: string;
  siteId?: number;
}

export interface MaintenanceTypeRequestModel {
  maintenanceTypeCode: string;
  maintenanceTypeName?: string;
  siteId?: number;
}

export interface MaintenanceIntervalDto {
  id: number;
  code: string;
  name: string;
  isTimeInterval: boolean;
  intervalValue: number;
  timeUnitCode?: string;
}

// --- Perform Maintenance Models ---

export interface MeasureWhenPerformedDto {
  id: number;
  code: string;
  name: string;
  description?: string;
}

export interface PerformMaintenanceRequestModel {
  maintenanceAssetScheduleId?: number;
  maintenanceCode?: string;
  maintenanceTypeId?: number;
  timeInterval?: boolean;
  performNonDate?: number;
  measureWhenPerformedId?: number;
  assetId: number;
  personId?: number;
  performDate: string | Date;
  laborCost?: number;
  partsCost?: number;
  notes?: string;
  maintenanceNotes?: string;
  advanceRecurrence: boolean;
  documentFileIds?: number[];
}

export interface PerformMaintenanceActionModel {
  id: number;
  personId?: number;
  laborCost?: number;
  partsCost?: number;
  notes?: string;
  maintenanceNotes?: string;
}

export interface PerformedMaintenanceDto {
  id: number;
  maintenanceAssetScheduleId?: number;
  maintenanceCode?: string;
  maintenanceTypeId?: number;
  maintenanceTypeName?: string;
  timeInterval?: boolean;
  performNonDate?: number;
  measureWhenPerformedId?: number;
  measureWhenPerformedName?: string;
  assetId: number;
  assetNumber?: string;
  assetName?: string;
  personId?: number;
  personName?: string;
  performDate?: string | Date;
  laborCost?: number;
  partsCost?: number;
  totalCost: number;
  notes?: string;
  maintenanceNotes?: string;
  operatorId?: string;
  documentFileIds?: number[];
  files?: any[];
}

export interface MaintenanceBlacklistCheckResultDto {
  isBlocked: boolean;
  message: string;
  overdueSchedules: MaintenanceAssetScheduleDto[];
}

export interface PerformedMaintenanceGridDataModel {
  reportId?: number;
  filter?: any;
  modelParam?: any;
  selectedIds?: number[];
  siteId?: number;
  assetId?: number;
  personId?: number;
  maintenanceTypeId?: number;
  dateFrom?: string | Date;
  dateTo?: string | Date;
}

export interface PerformedMaintenanceHistorySummaryDto {
  id: number;
  maintenanceAssetScheduleId?: number;
  assetId: number;
  scheduledForOtherInterval?: number;
  maintenanceCode?: string;
  scheduledFor?: string | Date;
  scheduledOn?: string | Date;
  isRecurring: boolean;
  frequency?: number;
  isPerformed: boolean;
  startDate?: string | Date;
  endDate?: string | Date;
  createDate?: string | Date;
  lastModifyDate?: string | Date;
  deleted: boolean;
  maintenanceTypeName?: string;
  maintenanceTypeCode?: string;
  timeInterval?: boolean;
  assetNumber?: string;
  assetName?: string;
  basisDate?: string | Date;
  basisNonDate?: number;
  siteCode?: string;
  siteName?: string;
  performedBy?: string;
  personName?: string;
  personId?: number;
  personCode?: string;
  scheduledBased?: boolean;
  interval?: string;
  maintenanceIntervalName?: string;
  serialNumber?: string;
  modelNumber?: string;
  modelName?: string;
  location?: string;
  locationCode?: string;
  locationDescription?: string;
  categoryId?: string;
  categoryCode?: string;
  categoryName?: string;
  flags?: string;
  notes?: string;
  fundingSourceId?: string;
  fundingSourceCode?: string;
  endOfLife?: string | Date;
  endOfSupport?: string | Date;
  inventoryDate?: string | Date;
  softwareExpiration?: string | Date;
  calibrationDate?: string | Date;
  lastPhysicalInventoryDate?: string | Date;
  lastCycleCountDate?: string | Date;
  performDate?: string | Date;
  laborCost?: number;
  partsCost?: number;
  totalCost?: number;
  maintenanceNotes?: string;
  operatorId?: string;
  performNonDate?: number;
  documentFileIds?: number[];
  files?: any[];

  // Relational objects matching GridFieldDefinitions navigation paths
  maintenanceAssetSchedule?: MaintenanceAssetScheduleDto;
  asset?: {
    id?: number;
    assetNumber?: string;
    assetName?: string;
    serialNumber?: string;
    endOfLife?: string | Date;
    endOfSupport?: string | Date;
    lastPhysicalInventoryDate?: string | Date;
    model?: {
      id?: number;
      number?: string;
      name?: string;
      modelInfo?: {
        id?: number;
        category?: {
          id?: number;
          code?: string;
          description?: string;
        };
      };
    };
    location?: {
      id?: number;
      code?: string;
      description?: string;
    };
    site?: {
      id?: number;
      code?: string;
      name?: string;
    };
    fundingSource?: {
      id?: number;
      fundingSourceId?: string;
    };
  };
  maintenanceType?: {
    id?: number;
    maintenanceTypeCode?: string;
    maintenanceTypeName?: string;
  };
  maintenanceInterval?: {
    id?: number;
    maintenanceIntervalCode?: string;
    maintenanceIntervalName?: string;
  };
  person?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    code?: string;
  };
  site?: {
    id?: number;
    code?: string;
    name?: string;
  };
}

// --- Stock Maintenance Models ---

export interface MaintenanceScheduleStockDto {
  id?: number;
  maintenanceAssetScheduleId?: number;
  stockId: number;
  stockNumber?: string;
  stockDescription?: string;
  requiredQuantity: number;
  cost?: number;
  extendedCost?: number;
  forecastedQuantity?: number;
  unitOfMeasureCode?: string;
  unitOfMeasureName?: string;
  siteId?: number;
}

export interface MaintenanceScheduleStockRequestModel {
  id?: number;
  stockId: number;
  requiredQuantity: number;
  cost?: number;
  extendedCost?: number;
}

